import type { Applicant, Payment } from "@/queries/payment";

type Status =
  | "PAID"
  | "PENDING"
  | "REJECTED"
  | "EXEMPT"
  | "EXPIRED"
  | "FAILED"
  | "CANCELLED";

export const getActivePayment = (payments: Payment[]) => {
  if (!payments || payments.length === 0) return null;
  return (
    payments.find((p) => p.status === "PAID") ??
    payments.find((p) => p.status === "PENDING") ??
    null
  );
};

export const getReferenceCode = (payments: Payment[]): string => {
  const payment = getActivePayment(payments);
  return (payment?.metadata?.internal_payment_reference as string) ?? "-";
};
export const formatCurrency = (amount: string | number) => {
  let numAmount: number;

  if (typeof amount === "string") {
    numAmount = Number.parseFloat(amount.replaceAll(",", ""));
  } else {
    numAmount = amount;
  }

  if (Number.isNaN(numAmount)) {
    return "$0.00";
  }

  return new Intl.NumberFormat("es-US", {
    style: "currency",
    currency: "USD",
  }).format(numAmount);
};

export const formatDate = (dateString: string | null) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formattedDate} | ${formattedTime}`;
};

export const getTotalColorClass = (status: Status): string => {
  switch (status) {
    case "PAID":
      return "text-success-quiet";
    case "EXEMPT":
      return "text-body-quiet";
    case "REJECTED":
      return "text-danger-quiet";
    case "PENDING":
    default:
      return "text-warning-quiet";
  }
};

export const getPaymentMethodLabel = (method: string | null): string => {
  if (!method) return "Pendiente";
  switch (method) {
    case "CREDIT_CARD":
      return "Tarjeta de crédito";
    case "NPE":
      return "NPE";
    default:
      return method;
  }
};

export const getApplicantName = (applicant: Applicant | null): string => {
  if (!applicant) return "Pendiente";
  const { first_name, last_name } = applicant;
  return `${first_name} ${last_name}`.trim() || "Pendiente";
};

export const getStatusColorClass = (status: Status): string => {
  if (status === "EXEMPT") return "quiet";
  if (status === "PAID") return "loud";
  return "quiet";
};

export const getPaymentDateValue = ({
  status,
  updated_at,
}: {
  status: Status;
  updated_at: string | null;
}): string | null => {
  if (status === "PAID" && updated_at) {
    return formatDate(updated_at);
  }
  return status === "EXEMPT" ? "Exonerado" : "Pendiente";
};

export const getPaymentMethodValue = (
  payments: Payment[],
  status: Status,
): string => {
  const activePayment = getActivePayment(payments);
  if (status === "PAID" && activePayment?.metadata?.payment_method_details) {
    return getPaymentMethodLabel(
      activePayment.metadata.payment_method_details as string,
    );
  }
  return status === "EXEMPT" ? "Exonerado" : "Pendiente";
};

export const getTotalLabel = (status: Status): string => {
  if (status === "PENDING") return "Total a pagar";
  return status === "EXEMPT" ? "Total exonerado" : "Total pagado";
};
